import React from 'react';
import { StatusBadge } from '../common';

/**
 * Component to display partnership overview statistics
 */
const PartnershipOverview = ({ partnershipData, isLoading }) => {
  if (isLoading) {
    return (
      <div className="bg-surface rounded-xl border border-border-medium shadow-sm p-6 mb-8">
        <h2 className="text-xl font-bold mb-4 text-text-primary">🤝 Partnership Overview</h2>
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!partnershipData || partnershipData.activePartnerships === 0) {
    return (
      <div className="bg-surface rounded-xl border border-border-medium shadow-sm p-6 mb-8">
        <h2 className="text-xl font-bold mb-4 text-text-primary">🤝 Partnership Overview</h2>
        <div className="flex justify-center items-center h-32 bg-surface-subtle rounded-lg">
          <div className="text-center">
            <p className="text-text-muted mb-2">No partnerships found</p>
            <p className="text-sm text-text-muted">Start playing with other players to build partnerships!</p>
          </div>
        </div>
      </div>
    );
  }

  const { activePartnerships, avgChemistryScore, partnershipWinRate } = partnershipData;

  // Determine chemistry score variant and label
  const getChemistryScoreDisplay = (score) => {
    if (score >= 0.8) return { variant: 'success', label: 'Excellent' };
    if (score >= 0.6) return { variant: 'warning', label: 'Good' };
    if (score >= 0.4) return { variant: 'warning', label: 'Fair' };
    return { variant: 'danger', label: 'Poor' };
  };

  const getWinRateColor = (rate) => {
    if (rate >= 70) return 'text-success-dark';
    if (rate >= 50) return 'text-warning-dark';
    return 'text-danger-dark';
  };

  const chemistryDisplay = getChemistryScoreDisplay(avgChemistryScore);
  const winRateColor = getWinRateColor(partnershipWinRate);

  return (
    <div className="bg-surface rounded-xl border border-border-medium shadow-sm p-6 mb-8">
      <h2 className="text-xl font-bold mb-4 text-text-primary">🤝 Partnership Overview</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="text-center">
          <div className="text-3xl font-bold text-info mb-2">
            {activePartnerships}
          </div>
          <div className="text-sm text-text-secondary">
            Active Partnerships
          </div>
          <div className="text-xs text-text-muted mt-1">
            Players you've formed partnerships with
          </div>
        </div>
        <div className="text-center">
          <div className="text-3xl font-bold text-text-primary mb-2">
            {avgChemistryScore.toFixed(2)}
          </div>
          <div className="text-sm text-text-secondary mb-1">
            Avg Chemistry Score
          </div>
          <StatusBadge variant={chemistryDisplay.variant} size="small">
            {chemistryDisplay.label}
          </StatusBadge>
        </div>
        <div className="text-center">
          <div className={`text-3xl font-bold ${winRateColor} mb-2`}>
            {partnershipWinRate.toFixed(1)}%
          </div>
          <div className="text-sm text-text-secondary">
            Partnership Win Rate
          </div>
          <div className="text-xs text-text-muted mt-1">
            When playing with partners
          </div>
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-border-medium">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="flex items-center">
            <div className="w-2 h-2 bg-info rounded-full mr-2"></div>
            <span className="text-text-secondary">
              <span className="font-medium text-text-primary">{activePartnerships}</span> partnerships tracked
            </span>
          </div>
          <div className="flex items-center">
            <div className="w-2 h-2 bg-success rounded-full mr-2"></div>
            <span className="text-text-secondary">
              Chemistry score range: <span className="font-medium text-text-primary">0.00 - 1.00</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnershipOverview;
